import moment from 'moment/moment';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { BQLink } from '../../../../atoms/bqlink';
import Loader from '../../../../atoms/icons/loader';
import EmptyState from '../empty-state';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import { isWithinTradingHours } from '../../../../../helpers/utils';
import './futures-rollover-external-widget.m.css';

const FuturesRolloverExternalWidget = ({ widget = false }) => {
  const widgetParam = widget ? '?view=apps' : '';
  const [rolloverData, setRolloverData] = useState([]);
  const [selectedFilter, setSelectedFilter] = useState('desc');
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const tradingHolidays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'trading_holidays'], []),
  );
  const specialTradingDays = useSelector((state) =>
    get(state, ['qt', 'config', 'features', 'special_trading_days'], {}),
  );

  const getRolloverData = async (sortOrder = 'desc') => {
    const response = await fetch(
      `/foapi/future/getByRollover?limit=200&order=${sortOrder === 'desc' ? '1' : '0'}`,
    );
    return response.json();
  };

  const resultRolloverData = useQuery(
    'getRolloverDataExternal',
    () => getRolloverData(selectedFilter),
    {
      refetchInterval: 30000,
      refetchIntervalInBackground: true,
      onSuccess: (data) => {
        setRolloverData(data?.data);
      },
      enabled:
        initialLoading ||
        (!!selectedFilter && isWithinTradingHours(tradingHolidays, specialTradingDays)),
    },
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialLoading(false);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (resultRolloverData?.isSuccess) {
      setRolloverData(resultRolloverData?.data?.data);
    }
  }, []);

  const clickHandler = async (type) => {
    setLoading(true);
    setSelectedFilter(type);
    const data = await getRolloverData(type);
    setRolloverData(data?.data);
    setLoading(false);
  };

  return (
    <React.Fragment>
      {initialLoading ? (
        <div styleName="initial-loading-container"></div>
      ) : (
        <div styleName="open-interest-container">
          <div styleName="btn-container">
            <button
              styleName={`filter-btn ${selectedFilter === 'desc' ? 'selected' : ''}`}
              onClick={() => clickHandler('desc')}
            >
              Highest Future Rollover
            </button>
            <button
              styleName={`filter-btn ${selectedFilter === 'asc' ? 'selected' : ''}`}
              onClick={() => clickHandler('asc')}
            >
              Lowest Future Rollover
            </button>
          </div>
          {rolloverData && rolloverData?.length !== 0 ? (
            <>
              {loading ? (
                <div styleName="loading-container-ext">
                  <div styleName="loading-container-int">
                    <Loader color={'#dadee1'} />
                  </div>
                </div>
              ) : (
                <div styleName="table-body-container">
                  <table styleName="main-table">
                    <thead styleName="table-header">
                      <tr>
                        <th styleName="column-title">Security</th>
                        <th styleName="column-title-alt">Expiry</th>
                        <th styleName="column-title-alt">Spot Price</th>
                        <th styleName="column-title-alt">% Rollover</th>

                        <th styleName="column-title-alt">
                          Open Interest <br />
                          (in contracts)
                        </th>
                        <th styleName="column-title-alt">
                          OI <br />
                          (% change)
                        </th>

                        <th styleName="column-title-alt">
                          Open Interest <br />
                          Turnover(Rs Cr)
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {rolloverData &&
                        rolloverData?.map((interestValue, index) => (
                          <tr styleName="data-row" key={index}>
                            <td>
                              {interestValue?.symbol ? (
                                <BQLink
                                  href={`/markets/futures-contract/${interestValue?.symbol}${widgetParam}`}
                                  target="_blank"
                                  rel="noreferrer"
                                >
                                  <span styleName="name">{interestValue?.symbol}</span>
                                </BQLink>
                              ) : (
                                <span styleName="name">-</span>
                              )}
                            </td>
                            <td styleName="column-value">
                              {moment(interestValue['expiry-date']).format('DD MMM')}
                            </td>
                            <td styleName="column-value">
                              {interestValue['spot-price'] ? interestValue['spot-price'] : '0'}
                            </td>
                            <td styleName="text-alignment">
                              {interestValue['rollover-percentage'] ? (
                                <span>
                                  {String(interestValue['rollover-percentage']).includes('-') ? (
                                    <span styleName="negative-value">
                                      {`${interestValue['rollover-percentage']}%`}
                                    </span>
                                  ) : (
                                    <span styleName="positive-value">
                                      {`+${interestValue['rollover-percentage']}%`}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span styleName="column-value">0.00%</span>
                              )}
                            </td>

                            <td styleName="column-value">
                              {interestValue['open-interest']
                                ? interestValue['open-interest']
                                : '0'}
                            </td>
                            <td styleName="text-alignment">
                              {interestValue['open-interest-change-percentage'] ? (
                                <span>
                                  {String(
                                    interestValue['open-interest-change-percentage'],
                                  ).includes('-') ? (
                                    <span styleName="negative-value">
                                      {`${interestValue['open-interest-change-percentage']?.toFixed(1)}%`}
                                    </span>
                                  ) : (
                                    <span styleName="positive-value">
                                      {`+${interestValue['open-interest-change-percentage']?.toFixed(1)}%`}
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span styleName="column-value">0.00%</span>
                              )}
                            </td>
                            <td styleName="column-value">
                              {interestValue['open-interest-turnover']
                                ? (interestValue['open-interest-turnover'] / 10000000)?.toFixed(2)
                                : '0'}
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          ) : (
            <div styleName="no-data-container-ext">
              <div styleName="no-data-container-int">
                <EmptyState />
              </div>
            </div>
          )}
        </div>
      )}
    </React.Fragment>
  );
};

export default FuturesRolloverExternalWidget;
